import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Facebook, Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { adfAssets } from "../storefront/assets";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { pickLocale } from "../../lib/format";
import { getFooterNavigation } from "../../lib/publicConfig";
import { useContactDetails } from "../../hooks/useSiteContent";
import { analytics } from "../../lib/analytics";

const columns = [
  /* These pointed at /category/suits|shirts|shoes|accessories — English handles
     from the original seed that this catalogue does not have, so all four
     footer links led to empty shelves. The live handles are the Azerbaijani
     slugs the importer creates. */
  { key: "products", title: "Məhsullar", links: [["footer.link.suits", "Kostyumlar", "/categories/kostyumlar"], ["footer.link.shirts", "Köynəklər", "/categories/koynekler"], ["footer.link.outerwear", "Xarici geyim", "/categories/xarici-geyim"], ["footer.link.trousers", "Şalvarlar", "/categories/salvarlar"]] },
  { key: "support", title: "Dəstək", links: [["footer.link.contact", "Əlaqə", "/contact"], ["footer.link.delivery", "Çatdırılma", "/delivery"], ["footer.link.returns", "Qaytarma", "/returns"], ["footer.link.gift_card", "Hədiyyə kartı", "/gift-card"]] },
  { key: "company", title: "Şirkət", links: [["footer.link.about", "Haqqımızda", "/about"], ["footer.link.stores", "Mağazalarımız", "/contact"], ["footer.link.journal", "Jurnal", "/journal"]] },
  { key: "social", title: "Sosial", links: [["footer.link.instagram", "Instagram", "/journal"], ["footer.link.facebook", "Facebook", "/journal"], ["footer.link.linkedin", "LinkedIn", "/journal"]] },
];

/* A configured link can point off-site — a social profile, a map — and a
   router Link would try to resolve that as an in-app route. */
function FooterLink({ to, className, children }) {
  if (/^https?:\/\//i.test(to || "")) {
    return <a href={to} target="_blank" rel="noreferrer" className={className}>{children}</a>;
  }
  return <Link to={to || "/"} className={className}>{children}</Link>;
}

/**
 * `showNewsletter` exists because the homepage has a newsletter section of its
 * own directly above the footer, and rendering the footer's as well put two
 * identical email forms within a few hundred pixels of each other. Every other
 * page keeps it.
 */
export default function Footer({ showNewsletter = true }) {
  const { locale, t } = useI18n();
  const details = useContactDetails();
  const [configured, setConfigured] = useState([]);

  /* The admin panel has offered `placement: footer` navigation since it was
     built, and the production database is seeded with five such rows — the
     footer never once fetched them. An editor could add, rename or remove a
     footer link and nothing happened. */
  useEffect(() => {
    let active = true;
    getFooterNavigation().then((rows) => {
      if (!active || !Array.isArray(rows) || !rows.length) return;
      const children = new Map();
      rows.forEach((row) => {
        if (!row.parent_id) return;
        if (!children.has(row.parent_id)) children.set(row.parent_id, []);
        children.get(row.parent_id).push(row);
      });
      const built = rows
        .filter((row) => !row.parent_id)
        .map((row) => ({
          key: row.id,
          title: pickLocale(row.title, locale) || "",
          links: (children.get(row.id) || []).map((child) => [
            child.id, pickLocale(child.title, locale) || "", child.link || "/",
          ]),
        }))
        .filter((column) => column.links.length);
      if (built.length) setConfigured(built);
    });
    return () => { active = false; };
  }, [locale]);

  /* Social links are real URLs once an editor supplies them; before this they
     were three labels all pointing at /journal. */
  const socialLinks = ["instagram", "facebook", "tiktok", "linkedin", "youtube"]
    .map((key) => [key, details?.[key]])
    .filter(([, url]) => url);

  /* Admin wins when it has something to say; the constant below is the
     fallback for an install that has never touched footer navigation. */
  const navColumns = configured.length ? configured : (socialLinks.length
    ? columns.map((column) => (column.key === "social"
      ? { ...column, links: socialLinks.map(([key, url]) => [`footer.link.${key}`, key[0].toUpperCase() + key.slice(1), url]) }
      : column))
    : columns);
  const phone = details?.phone;
  const email = details?.email;
  const addressLines = (pickLocale(details?.address, locale) || "").split("\n").filter(Boolean);
  const hoursLines = (
    (typeof details?.hours === "string" ? details.hours : pickLocale(details?.hours, locale)) || ""
  ).split("\n").filter(Boolean);
  const social = [
    ["facebook", Facebook, details?.facebook],
    ["instagram", Instagram, details?.instagram],
    ["whatsapp", MessageCircle, details?.whatsapp ? `https://wa.me/${String(details.whatsapp).replace(/[^0-9]/g, "")}` : null],
  ].filter(([, , url]) => url);

  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="bg-white text-charcoal pt-16 pb-12 border-t border-charcoal/10">
      <div className="container mx-auto px-6 md:px-12">
        {showNewsletter && (
          <div className="mb-16 max-w-md">
            <h4 className="text-[13px] uppercase tracking-[0.1em] font-medium mb-6">
              {t("footer.newsletter.title", "Bülleten")}
            </h4>
            <NewsletterSignup compact tone="light" />
          </div>
        )}

        {/* Five columns: three an editor owns, then contact, then payment and
            social. The reference lays them out flat at every width rather than
            collapsing the link lists into accordions. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-20">
          {navColumns.slice(0, 3).map((column, index) => (
            <div key={column.key}>
              {/* The brand column leads with the mark rather than the name set
                  as type — the wordmark is the logo, and printing it as a
                  heading beside three ordinary ones flattened it into a label.
                  The name stays as the alt text, so screen readers and search
                  engines lose nothing. */}
              {index === 0 ? (
                /* Başlıqdakı ilə eyni cütlük — monoqram + söz nişanı. Tək söz
                   nişanı adi mətn sətri kimi oxunurdu. */
                <div className="flex items-center gap-2.5 mb-6">
                  <img src={adfAssets.logoMono} alt="" className="h-7 w-auto" />
                  <img
                    src={adfAssets.logoWord}
                    alt="Atelier Di Falco"
                    className="h-5 w-auto max-w-[168px]"
                  />
                </div>
              ) : (
                <h4 className="text-[13px] uppercase tracking-[0.1em] font-medium mb-6">
                  {t(`footer.${column.key}`, column.title)}
                </h4>
              )}
              <ul className="space-y-4 text-[13px] font-medium text-charcoal/90">
                {column.links.map(([key, label, to]) => (
                  <li key={key}>
                    <FooterLink to={to} className="hover:text-gold transition-colors">
                      {t(key, label)}
                    </FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="text-[13px] uppercase tracking-[0.1em] font-medium mb-6">
              {t("footer.contact", "Əlaqə")}
            </h4>
            {/* Hər sətir öz işarəsi ilə — telefon, poçt, ünvan, saatlar bir
                siyahıda fərqlənmirdi; işarə sətri oxumadan tanıdır. */}
            <ul className="space-y-4 text-[13px] font-medium text-charcoal/90">
              {phone && (
                <li>
                  <a
                    href={`tel:${String(phone).replace(/\s/g, "")}`}
                    className="flex items-center gap-2.5 hover:text-gold transition-colors"
                  >
                    <Phone size={15} strokeWidth={1.4} className="shrink-0 text-charcoal/60" aria-hidden="true" />
                    <span>{phone}</span>
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a
                    href={`mailto:${email}`}
                    className="flex items-center gap-2.5 hover:text-gold transition-colors"
                  >
                    <Mail size={15} strokeWidth={1.4} className="shrink-0 text-charcoal/60" aria-hidden="true" />
                    <span className="break-all">{email}</span>
                  </a>
                </li>
              )}
              {addressLines.length > 0 && (
                <li className="flex items-start gap-2.5">
                  <MapPin size={15} strokeWidth={1.4} className="mt-0.5 shrink-0 text-charcoal/60" aria-hidden="true" />
                  <span>
                    {addressLines.map((line, i) => (
                      <React.Fragment key={line}>
                        {i > 0 && <br />}
                        {line}
                      </React.Fragment>
                    ))}
                  </span>
                </li>
              )}
              {hoursLines.length > 0 && (
                <li className="flex items-start gap-2.5">
                  <Clock size={15} strokeWidth={1.4} className="mt-0.5 shrink-0 text-charcoal/60" aria-hidden="true" />
                  <span>
                    {hoursLines.map((line, i) => (
                      <React.Fragment key={line}>
                        {i > 0 && <br />}
                        {line}
                      </React.Fragment>
                    ))}
                  </span>
                </li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="text-[13px] uppercase tracking-[0.1em] font-medium mb-6">
              {t("footer.payment", "Ödəniş")}
            </h4>
            <div className="flex space-x-3 mb-8">
              <div className="w-[50px] h-8 border border-charcoal/10 rounded-none flex items-center justify-center bg-white" aria-label="Mastercard">
                <div className="flex -space-x-1.5">
                  <div className="w-4 h-4 rounded-full bg-[#eb001b] mix-blend-multiply opacity-90" />
                  <div className="w-4 h-4 rounded-full bg-[#f79e1b] mix-blend-multiply opacity-90" />
                </div>
              </div>
              <div className="w-[50px] h-8 border border-charcoal/10 rounded-none flex items-center justify-center bg-white text-[11px] font-bold text-[#1a1f71] italic tracking-tighter">
                VISA
              </div>
            </div>

            {social.length > 0 && (
              <>
                <h4 className="text-[13px] uppercase tracking-[0.1em] font-medium mb-6">
                  {t("footer.social", "Sosial media")}
                </h4>
                <div className="flex space-x-3">
                  {social.map(([key, Icon, url]) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={key}
                      className="w-10 h-10 border border-charcoal/30 flex items-center justify-center hover:bg-charcoal hover:text-white hover:border-charcoal transition-colors"
                    >
                      <Icon size={18} strokeWidth={1} aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* The reference prints only a copyright line. The two policy links stay
            beside it: they are a legal requirement, not decoration. */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-3 text-[13px] text-charcoal/90 font-medium border-t border-charcoal/10">
          <p>&copy; Copyright {year} Atelier Di Falco.</p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/privacy" className="hover:text-gold transition-colors">
              {t("footer.privacy", "Məxfilik siyasəti")}
            </Link>
            <Link to="/terms" className="hover:text-gold transition-colors">
              {t("footer.terms", "İstifadə şərtləri")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * `tone` is which ground this sits on, not which colour the type is. The form
 * was written for the ink-dark footer and hardcoded warm-white text, border and
 * consent label; dropped onto the light homepage section it rendered white on
 * white — the field and its label were invisible and only the button showed.
 */
export function NewsletterSignup({ compact = false, tone = "dark" }) {
  const onDark = tone === "dark";
  const line = onDark ? "rgba(255,252,249,0.72)" : "var(--adf-border-strong)";
  const field = onDark ? "var(--adf-white-warm)" : "var(--adf-ink)";
  const muted = onDark ? "rgba(255,252,249,0.68)" : "var(--adf-gray-2)";
  const button = onDark
    ? "bg-[var(--adf-white-warm)] text-[var(--adf-ink)] hover:bg-[var(--adf-stone)]"
    : "bg-[var(--adf-ink)] text-[var(--adf-white-warm)] hover:bg-[var(--adf-espresso)]";

  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setMessage("");
    if (!email.trim() || !consent) {
      setMessage(t("footer.newsletter.required", "E-poçt ünvanını yazın və razılığı təsdiqləyin."));
      return;
    }
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", {
        email: email.trim(),
        consent: true,
        source: compact ? "footer" : "homepage",
      });
      analytics.lead("newsletter");
      setEmail("");
      setConsent(false);
      setMessage(t("footer.newsletter.success", "Abunəliyiniz təsdiqləndi."));
    } catch (error) {
      setMessage(error?.response?.data?.detail || t("footer.newsletter.error", "Abunəlik tamamlanmadı."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className={compact ? "mt-8 max-w-md" : "mt-8"} data-testid="newsletter-form">
      <div className="flex border" style={{ borderColor: line }}>
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="min-w-0 flex-1 bg-transparent px-4 py-4 text-sm outline-none"
          style={{ color: field }}
          placeholder={t("footer.newsletter.placeholder", "E-poçt ünvanı")}
          aria-label={t("footer.newsletter.placeholder", "E-poçt ünvanı")}
        />
        <button disabled={busy} className={`min-w-[104px] px-5 adf-small transition disabled:opacity-60 ${button}`}>
          {busy ? "..." : t("footer.newsletter.submit", "Göndər")}
        </button>
      </div>
      <label className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-3 text-left text-[12px] leading-5" style={{ color: muted }}>
        <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} className="mt-1 h-4 w-4 accent-[var(--adf-white-warm)]" />
        <span>{t("footer.newsletter.consent", "Yenilikləri və şəxsi təklifləri e-poçtla almağa razıyam.")}</span>
      </label>
      {message && <div className="mt-3 text-left text-xs" style={{ color: muted }} role="status">{message}</div>}
    </form>
  );
}
